/*
 * dc-gift-priority.js
 *
 * 역할
 * 1. 증정 상품의 재고를 우선순위대로 확인
 * 2. 재고가 있는 첫 번째 variant 자동 선택
 * 3. 고객의 증정품 드롭다운 조작 차단
 * 4. 장바구니 추가 직전 최신 재고 재확인
 * 5. ComboWise의 기존 장바구니 추가 로직 실행
 *
 * 필수 data 속성
 * - data-mount="gift-priority-selector"
 * - data-dc-option-target-title
 * - data-dc-option-product-handle
 * - data-dc-option-inventory
 */

(function () {
    "use strict";

    window.App = window.App || {};
    window.App.modules = window.App.modules || {};

    window.App.modules["gift-priority-selector"] = function (root) {
        /*
         * data-dc-option-debug="false"로 설정하면
         * 디버깅 로그를 끌 수 있음.
         */
        const DEBUG =
            root.dataset.dcOptionDebug !== "false";

        const targetTitle =
            root.dataset.dcOptionTargetTitle || "";

        const giftProductHandle =
            root.dataset.dcOptionProductHandle || "";

        const inventorySectionId =
            root.dataset.dcOptionInventorySection ||
            "dc-gift-inventory";

        const parsedSafetyStock = Number.parseInt(
            root.dataset.dcOptionSafetyStock,
            10
        );

        const safetyStock = Number.isFinite(parsedSafetyStock)
            ? Math.max(0, parsedSafetyStock)
            : 20;

        /*
         * 재고 소진 우선순위
         */
        const priority = [
            "ランダムポリッシュ",
            "ランダムポリッシュ2",
            "ランダムポリッシュ3",
            "ランダムポリッシュ4",
            "ランダムポリッシュ5",
            "ランダムポリッシュ6",
            "ランダムポリッシュ7",
            "ランダムポリッシュ8"
        ];

        const state = {
            destroyed: false,
            applyingVariant: false,
            initialSelectionRunning: false
        };

        let observer = null;
        let observerRoot = null;
        let observerFrame = null;

        let appRoot = null;
        let giftRow = null;
        let giftSelect = null;
        let addToCartButton = null;
        let errorElement = null;

        /*
         * 디버깅
         */
        function debugLog(...args) {
            if (!DEBUG) return;

            console.log(
                "[gift-priority-selector]",
                ...args
            );
        }

        function debugWarn(...args) {
            if (!DEBUG) return;

            console.warn(
                "[gift-priority-selector]",
                ...args
            );
        }

        function debugError(...args) {
            console.error(
                "[gift-priority-selector]",
                ...args
            );
        }

        /*
         * 텍스트 비교용 정규화
         */
        function normalizeText(value = "") {
            return String(value)
                .replace(/^[\s˙·•]+/, "")
                .replace(/\s+/g, " ")
                .trim()
                .toLowerCase();
        }

        /*
         * Shopify 섹션 또는 부모 영역을 우선 검색 범위로 사용
         */
        function getSearchScope() {
            return (
                root.closest(".shopify-section") ||
                root.parentElement ||
                document
            );
        }

        /*
         * 중복 요소 제거
         */
        function getUniqueElements(elements) {
            return Array.from(new Set(elements));
        }

        /*
         * 특정 ComboWise 영역에서 증정 행 탐색
         */
        function findGiftRowInApp(candidateAppRoot) {
            if (!candidateAppRoot) return null;

            const normalizedTargetTitle =
                normalizeText(targetTitle);

            const rows = Array.from(
                candidateAppRoot.querySelectorAll(
                    ".combowise-mixmatch_row"
                )
            );

            debugLog("ComboWise 행 확인", {
                appRoot: candidateAppRoot,
                targetTitle,
                rowCount: rows.length,
                rowTitles: rows.map((row) => {
                    return row
                        .querySelector(
                            ".combowise-mixmatch_title"
                        )
                        ?.textContent
                        ?.trim();
                })
            });

            return rows.find((row) => {
                const titleElement = row.querySelector(
                    ".combowise-mixmatch_title"
                );

                const currentTitle = normalizeText(
                    titleElement?.textContent
                );

                const matched =
                    currentTitle ===
                    normalizedTargetTitle;

                debugLog("증정 행 제목 비교", {
                    originalTitle:
                        titleElement?.textContent?.trim(),
                    normalizedTitle: currentTitle,
                    targetTitle: normalizedTargetTitle,
                    matched
                });

                return matched;
            }) || null;
        }

        /*
         * ComboWise 요소 탐색
         */
        function findGiftElements() {
            const searchScope = getSearchScope();

            const candidates = getUniqueElements([
                ...(root.matches?.(".combowise-mixmatch")
                    ? [root]
                    : []),
                ...Array.from(
                    root.querySelectorAll?.(
                        ".combowise-mixmatch"
                    ) || []
                ),
                ...Array.from(
                    searchScope.querySelectorAll?.(
                        ".combowise-mixmatch"
                    ) || []
                ),
                ...Array.from(
                    document.querySelectorAll(
                        ".combowise-mixmatch"
                    )
                )
            ]);

            debugLog("요소 탐색 시작", {
                moduleRoot: root,
                searchScope,
                targetTitle,
                candidateCount: candidates.length
            });

            let foundAppRoot = null;
            let foundGiftRow = null;

            for (const candidate of candidates) {
                const candidateGiftRow =
                    findGiftRowInApp(candidate);

                if (!candidateGiftRow) {
                    continue;
                }

                foundAppRoot = candidate;
                foundGiftRow = candidateGiftRow;
                break;
            }

            if (!foundAppRoot || !foundGiftRow) {
                debugWarn(
                    "증정용 ComboWise 영역을 찾지 못함",
                    {
                        targetTitle,
                        candidates
                    }
                );

                return null;
            }

            const foundGiftSelect =
                foundGiftRow.querySelector("select");

            const foundButton =
                foundAppRoot.querySelector(
                    '[id^="combowise-ATC_"]'
                );

            const foundErrorElement =
                foundAppRoot.querySelector(
                    ".combowise-mixmatch_error-message"
                );

            if (
                !foundGiftSelect ||
                !foundButton
            ) {
                debugWarn(
                    "필수 요소를 찾지 못함",
                    {
                        appRoot: foundAppRoot,
                        giftRow: foundGiftRow,
                        giftSelect: foundGiftSelect,
                        button: foundButton
                    }
                );

                return null;
            }

            return {
                appRoot: foundAppRoot,
                giftRow: foundGiftRow,
                giftSelect: foundGiftSelect,
                addToCartButton: foundButton,
                errorElement: foundErrorElement
            };
        }

        /*
         * 외부 앱 DOM에 스타일 클래스와 JS hook 부여
         */
        function applyElementHooks(elements) {
            elements.appRoot.classList.add(
                "dc-gift-priority"
            );

            elements.giftRow.classList.add(
                "dc-gift-priority__row"
            );

            elements.giftSelect.classList.add(
                "dc-gift-priority__select",
                "dc-gift-priority__select--locked"
            );

            elements.addToCartButton.classList.add(
                "dc-gift-priority__button"
            );

            /*
             * JS 제어 전용 hook
             */
            elements.giftSelect.dataset.dcOptionGiftSelect =
                "";

            elements.addToCartButton.dataset.dcAction =
                "add-priority-gift";

            /*
             * 실제 disabled는 사용하지 않음.
             * disabled select는 앱의 데이터 수집에서 누락될 수 있음.
             */
            elements.giftSelect.setAttribute(
                "aria-disabled",
                "true"
            );

            elements.giftSelect.setAttribute(
                "tabindex",
                "-1"
            );

            debugLog("hook 및 클래스 부여 완료", {
                appRootClasses: [
                    ...elements.appRoot.classList
                ],
                giftRowClasses: [
                    ...elements.giftRow.classList
                ],
                giftSelectClasses: [
                    ...elements.giftSelect.classList
                ],
                buttonClasses: [
                    ...elements.addToCartButton.classList
                ],
                giftSelectDataset: {
                    ...elements.giftSelect.dataset
                },
                buttonDataset: {
                    ...elements.addToCartButton.dataset
                }
            });

            debugCssState(elements.giftSelect);
        }

        /*
         * CSS 실제 적용 상태 확인
         */
        function debugCssState(selectElement) {
            if (!DEBUG) return;

            requestAnimationFrame(() => {
                if (
                    state.destroyed ||
                    !selectElement.isConnected
                ) {
                    return;
                }

                const computedStyle =
                    window.getComputedStyle(
                        selectElement
                    );

                const selectorMatched =
                    selectElement.matches(
                        ".dc-gift-priority " +
                        ".dc-gift-priority__select--locked"
                    );

                console.groupCollapsed(
                    "🧪 [gift-priority-selector] CSS 적용 확인"
                );

                console.log(
                    "select 요소:",
                    selectElement
                );

                console.log(
                    "상위 dc-gift-priority 존재:",
                    Boolean(
                        selectElement.closest(
                            ".dc-gift-priority"
                        )
                    )
                );

                console.log(
                    "잠금 클래스 존재:",
                    selectElement.classList.contains(
                        "dc-gift-priority__select--locked"
                    )
                );

                console.log(
                    "CSS 선택자 매칭:",
                    selectorMatched
                );

                console.log(
                    "pointer-events:",
                    computedStyle.pointerEvents
                );

                console.log(
                    "cursor:",
                    computedStyle.cursor
                );

                console.log(
                    "opacity:",
                    computedStyle.opacity
                );

                console.log(
                    "aria-disabled:",
                    selectElement.getAttribute(
                        "aria-disabled"
                    )
                );

                console.log(
                    "tabindex:",
                    selectElement.getAttribute(
                        "tabindex"
                    )
                );

                console.log(
                    "현재 선택값:",
                    selectElement.value
                );

                console.groupEnd();

                if (
                    computedStyle.pointerEvents !==
                    "none"
                ) {
                    debugWarn(
                        "드롭다운 잠금 CSS가 적용되지 않음",
                        {
                            expected:
                                "pointer-events: none",
                            actual:
                                computedStyle.pointerEvents,
                            selectElement
                        }
                    );

                    return;
                }

                debugLog(
                    "✅ 드롭다운 CSS 잠금 정상 적용"
                );
            });
        }

        /*
         * Liquid에서 최초 렌더링한 재고 데이터 파싱
         */
        function parseInitialInventory() {
            const inventoryElement =
                root.querySelector(
                    "[data-dc-option-inventory]"
                );

            if (!inventoryElement) {
                debugWarn(
                    "초기 재고 JSON 요소를 찾지 못함"
                );

                return [];
            }

            try {
                const parsedData = JSON.parse(
                    inventoryElement.textContent
                );

                const variants =
                    Array.isArray(parsedData.variants)
                        ? parsedData.variants
                        : [];

                debugLog("초기 재고 데이터", variants);

                return variants;
            } catch (error) {
                debugError(
                    "초기 재고 JSON 파싱 실패",
                    error
                );

                return [];
            }
        }

        /*
         * 우선순위에 맞는 재고 보유 variant 탐색
         */
        function findPriorityVariant(variants) {
            const variantMap = new Map(
                variants.map((variant) => [
                    normalizeText(variant.title),
                    variant
                ])
            );

            const inventoryStatus = priority.map((title) => {
                const variant = variantMap.get(
                    normalizeText(title)
                );

                const inventoryQuantity =
                    Number(variant?.inventoryQuantity) || 0;

                return {
                    priorityTitle: title,
                    variantId: variant?.id || null,
                    sku: variant?.sku || null,
                    inventoryQuantity,
                    safetyStock,
                    available:
                        Boolean(variant) &&
                        inventoryQuantity > safetyStock
                };
            });

            debugLog(
                "우선순위별 재고 상태",
                inventoryStatus
            );

            for (const title of priority) {
                const variant = variantMap.get(
                    normalizeText(title)
                );

                if (!variant) {
                    continue;
                }

                const inventoryQuantity =
                    Number(variant.inventoryQuantity) || 0;

                /*
                * 안전재고보다 많은 경우에만 선택
                *
                * 안전재고 5개라면:
                * 재고 5개 이하 → 다음 옵션
                * 재고 6개 이상 → 선택 가능
                */
                if (inventoryQuantity > safetyStock) {
                    debugLog(
                        "우선순위 variant 선택",
                        {
                            ...variant,
                            safetyStock
                        }
                    );

                    return variant;
                }
            }

            debugWarn(
                "안전재고 기준을 충족하는 variant 없음",
                {
                    safetyStock
                }
            );

            return null;
        }

        /*
         * select 값을 native setter로 변경
         */
        function setSelectNativeValue(
            selectElement,
            value
        ) {
            const valueSetter =
                Object.getOwnPropertyDescriptor(
                    HTMLSelectElement.prototype,
                    "value"
                )?.set;

            if (valueSetter) {
                valueSetter.call(
                    selectElement,
                    String(value)
                );

                return;
            }

            selectElement.value = String(value);
        }

        /*
         * ComboWise 증정 variant 선택
         */
        function setGiftVariant(
            selectElement,
            variant,
            options = {}
        ) {
            const {
                forceEvent = false,
                source = "unknown"
            } = options;

            const variantId = String(variant.id);

            const optionElement = Array.from(
                selectElement.options
            ).find((option) => {
                return (
                    String(option.value) ===
                    variantId
                );
            });

            if (!optionElement) {
                throw new Error(
                    `ComboWise 옵션에 variant가 없습니다: ${variantId}`
                );
            }

            const previousValue =
                String(selectElement.value);

            const valueChanged =
                previousValue !== variantId;

            state.applyingVariant = true;

            try {
                setSelectNativeValue(
                    selectElement,
                    variantId
                );

                selectElement.dataset.dcOptionLockedValue =
                    variantId;

                /*
                 * ComboWise 내부 선택값 및 이미지 갱신
                 */
                if (valueChanged || forceEvent) {
                    selectElement.dispatchEvent(
                        new Event("input", {
                            bubbles: true
                        })
                    );

                    selectElement.dispatchEvent(
                        new Event("change", {
                            bubbles: true
                        })
                    );
                }
            } finally {
                state.applyingVariant = false;
            }

            debugLog("증정 variant 적용 완료", {
                source,
                previousValue,
                selectedValue: selectElement.value,
                variantId,
                title: variant.title,
                sku: variant.sku,
                inventoryQuantity:
                    variant.inventoryQuantity,
                valueChanged,
                eventDispatched:
                    valueChanged || forceEvent
            });
        }

        /*
         * 고객 또는 다른 스크립트가 잠금값을 변경했을 때 복원
         */
        function restoreLockedValue() {
            if (
                state.destroyed ||
                state.applyingVariant ||
                !giftSelect
            ) {
                return;
            }

            const hasLockedValue =
                Object.prototype.hasOwnProperty.call(
                    giftSelect.dataset,
                    "dcOptionLockedValue"
                );

            if (!hasLockedValue) {
                return;
            }

            const lockedValue =
                giftSelect.dataset.dcOptionLockedValue;

            if (
                String(giftSelect.value) ===
                String(lockedValue)
            ) {
                return;
            }

            debugWarn(
                "잠금된 증정 옵션 변경 감지",
                {
                    currentValue: giftSelect.value,
                    lockedValue
                }
            );

            state.applyingVariant = true;

            try {
                setSelectNativeValue(
                    giftSelect,
                    lockedValue
                );

                giftSelect.dispatchEvent(
                    new Event("input", {
                        bubbles: true
                    })
                );

                giftSelect.dispatchEvent(
                    new Event("change", {
                        bubbles: true
                    })
                );
            } finally {
                state.applyingVariant = false;
            }

            debugLog(
                "증정 옵션을 잠금값으로 복원",
                lockedValue
            );
        }

        /*
         * 드롭다운 직접 조작 차단
         */
        function preventSelectInteraction(event) {
            if (state.applyingVariant) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();

            giftSelect?.blur();

            debugLog(
                "고객의 증정 드롭다운 조작 차단",
                event.type
            );
        }

        /*
         * 기존 ComboWise 오류 영역 또는 커스텀 메시지 탐색
         */
        function getErrorElement() {
            if (
                errorElement &&
                errorElement.isConnected
            ) {
                return errorElement;
            }

            const existingElement =
                appRoot?.querySelector(
                    "[data-dc-component=" +
                    '"gift-priority-message"]'
                );

            if (existingElement) {
                errorElement = existingElement;
                return errorElement;
            }

            if (!giftRow) return null;

            const createdElement =
                document.createElement("p");

            createdElement.className =
                "dc-gift-priority__message";

            createdElement.dataset.dcComponent =
                "gift-priority-message";

            giftRow.appendChild(createdElement);

            errorElement = createdElement;

            return errorElement;
        }

        /*
         * 오류 메시지 표시
         */
        function showError(message) {
            const messageElement =
                getErrorElement();

            if (!messageElement) {
                debugWarn(
                    "오류 메시지를 표시할 요소가 없음",
                    message
                );

                return;
            }

            messageElement.textContent = message;

            messageElement.classList.add(
                "dc-gift-priority__message"
            );

            messageElement.classList.remove(
                "combowise-mixmatch_hide"
            );

            messageElement.hidden = false;

            debugWarn("오류 메시지 표시", message);
        }

        /*
         * 오류 메시지 초기화
         */
        function clearError() {
            const messageElement =
                getErrorElement();

            if (!messageElement) return;

            messageElement.textContent = "";

            if (
                messageElement.classList.contains(
                    "combowise-mixmatch_error-message"
                )
            ) {
                messageElement.classList.add(
                    "combowise-mixmatch_hide"
                );
            } else {
                messageElement.hidden = true;
            }

            debugLog("오류 메시지 초기화");
        }

        function showGiftUnavailableUI() {
            if (!giftRow || !giftSelect) return;

            giftRow.classList.add(
                "dc-gift-priority__row--unavailable"
            );

            /*
            * 기존 Choose option 선택
            */
            state.applyingVariant = true;

            try {
                setSelectNativeValue(
                    giftSelect,
                    ""
                );

                giftSelect.dataset.dcOptionLockedValue = "";

                giftSelect.dispatchEvent(
                    new Event("input", {
                        bubbles: true
                    })
                );

                giftSelect.dispatchEvent(
                    new Event("change", {
                        bubbles: true
                    })
                );
            } finally {
                state.applyingVariant = false;
            }

            let messageElement = giftRow.querySelector(
                '[data-dc-component="gift-unavailable-message"]'
            );

            if (!messageElement) {
                messageElement =
                    document.createElement("p");

                messageElement.className =
                    "dc-gift-priority__message";

                messageElement.dataset.dcComponent =
                    "gift-unavailable-message";

                giftRow.querySelector('.combowise-mixmatch_caption').prepend(messageElement);
            }

            messageElement.textContent =
                "特典ポリッシュは終了いたしました。";

            messageElement.hidden = false;

            debugLog(
                "증정품 품절 UI 표시"
            );
        }

        function clearGiftUnavailableUI() {
            if (!giftRow || !giftSelect) return;

            giftRow.classList.remove(
                "dc-gift-priority__row--unavailable"
            );

            const messageElement = giftRow.querySelector(
                '[data-dc-component="gift-unavailable-message"]'
            );

            if (messageElement) {
                messageElement.hidden = true;
                messageElement.textContent = "";
            }

            debugLog(
                "증정품 품절 UI 해제"
            );
        }        

        /*
         * 버튼 비활성화
         */
        function disableAddToCart(reason) {
            if (!addToCartButton) return;

            addToCartButton.disabled = true;

            addToCartButton.setAttribute(
                "aria-disabled",
                "true"
            );

            addToCartButton.classList.add(
                "dc-gift-priority__button--disabled"
            );

            addToCartButton.dataset
                .dcDisabledByGiftPriority = "true";

            addToCartButton.dataset
                .dcOptionDisabledReason = reason;

            debugLog("장바구니 버튼 비활성화", {
                reason
            });
        }

        /*
         * 이 모듈이 비활성화한 버튼만 복원
         */
        function enableAddToCart() {
            if (!addToCartButton) return;

            if (
                addToCartButton.dataset
                    .dcDisabledByGiftPriority !==
                "true"
            ) {
                return;
            }

            addToCartButton.disabled = false;

            addToCartButton.removeAttribute(
                "aria-disabled"
            );

            addToCartButton.classList.remove(
                "dc-gift-priority__button--disabled"
            );

            delete addToCartButton.dataset
                .dcDisabledByGiftPriority;

            delete addToCartButton.dataset
                .dcOptionDisabledReason;

            debugLog("장바구니 버튼 활성화");
        }

        /*
         * Section Rendering API를 통한 최신 재고 조회
         */
        async function fetchLatestInventory() {
            if (!giftProductHandle) {
                throw new Error(
                    "data-dc-option-product-handle이 비어 있습니다."
                );
            }

            const shopRoot =
                window.Shopify?.routes?.root ||
                "/";

            const url = new URL(
                `${shopRoot}products/${encodeURIComponent(
                    giftProductHandle
                )}`,
                window.location.origin
            );

            url.searchParams.set(
                "section_id",
                inventorySectionId
            );

            /*
             * 브라우저 및 CDN 캐시 방지용
             */
            url.searchParams.set(
                "_dc",
                String(Date.now())
            );

            debugLog("최신 재고 조회 시작", {
                url: url.toString(),
                handle: giftProductHandle,
                sectionId: inventorySectionId
            });

            const response = await fetch(
                url.toString(),
                {
                    method: "GET",
                    cache: "no-store",
                    credentials: "same-origin",
                    headers: {
                        "X-Requested-With":
                            "XMLHttpRequest"
                    }
                }
            );

            debugLog("최신 재고 응답", {
                status: response.status,
                ok: response.ok
            });

            if (!response.ok) {
                throw new Error(
                    `재고 조회 실패: HTTP ${response.status}`
                );
            }

            const html = await response.text();

            const parsedDocument =
                new DOMParser().parseFromString(
                    html,
                    "text/html"
                );

            const inventoryElement =
                parsedDocument.querySelector(
                    "[data-dc-option-inventory]"
                );

            if (!inventoryElement) {
                debugError(
                    "재고 응답 원문",
                    html
                );

                throw new Error(
                    "재고 응답에서 data-dc-option-inventory를 찾지 못했습니다."
                );
            }

            let inventoryData;

            try {
                inventoryData = JSON.parse(
                    inventoryElement.textContent
                );
            } catch (error) {
                throw new Error(
                    `최신 재고 JSON 파싱 실패: ${error.message}`
                );
            }

            if (
                !Array.isArray(
                    inventoryData.variants
                )
            ) {
                throw new Error(
                    "최신 재고 데이터에 variants 배열이 없습니다."
                );
            }

            debugLog(
                "최신 재고 조회 완료",
                inventoryData.variants
            );

            return inventoryData.variants;
        }

        /*
         * 최초 페이지 진입 시 자동 선택
         */
        async function applyInitialSelection() {
            if (
                state.destroyed ||
                state.initialSelectionRunning ||
                !giftSelect
            ) {
                return;
            }

            if (
                giftSelect.dataset
                    .dcOptionPriorityInitialized ===
                "true"
            ) {
                return;
            }

            state.initialSelectionRunning = true;

            try {
                let variants =
                    parseInitialInventory();

                /*
                 * 초기 Liquid 데이터가 없으면
                 * 최신 Section 데이터로 대체
                 */
                if (!variants.length) {
                    variants =
                        await fetchLatestInventory();
                }

                if (state.destroyed) return;

                const selectedVariant =
                    findPriorityVariant(variants);

                if (!selectedVariant) {
                    clearError();
                    enableAddToCart();

                    showGiftUnavailableUI();

                    giftSelect.dataset
                        .dcOptionPriorityInitialized = "true";

                    debugLog(
                        "증정 가능 재고 없음: 증정품 없이 진행"
                    );

                    return;
                }

                clearError();
                enableAddToCart();
                clearGiftUnavailableUI();

                setGiftVariant(
                    giftSelect,
                    selectedVariant,
                    {
                        forceEvent: true,
                        source: "initial"
                    }
                );

                giftSelect.dataset
                    .dcOptionPriorityInitialized =
                    "true";

                debugLog(
                    "✅ 최초 증정 옵션 자동 선택 완료",
                    selectedVariant
                );
            } catch (error) {
                debugError(
                    "최초 증정 옵션 선택 실패",
                    error
                );

                showError(
                    "プレゼント商品の在庫確認に失敗しました。ページを再読み込みしてください。"
                );
            } finally {
                state.initialSelectionRunning = false;
            }
        }

        /*
         * DOM 요소 이벤트 해제
         */
        function unbindCurrentElements() {
            if (giftSelect) {
                giftSelect.removeEventListener(
                    "change",
                    restoreLockedValue,
                    true
                );

                giftSelect.removeEventListener(
                    "pointerdown",
                    preventSelectInteraction,
                    true
                );

                giftSelect.removeEventListener(
                    "mousedown",
                    preventSelectInteraction,
                    true
                );

                giftSelect.removeEventListener(
                    "touchstart",
                    preventSelectInteraction,
                    true
                );

                giftSelect.removeEventListener(
                    "keydown",
                    preventSelectInteraction,
                    true
                );
            }
        }

        /*
         * 새로 발견된 DOM 요소에 이벤트 등록
         */
        function bindGiftElements() {
            const elements =
                findGiftElements();

            if (!elements) {
                return false;
            }

            const elementsUnchanged =
                appRoot === elements.appRoot &&
                giftRow === elements.giftRow &&
                giftSelect ===
                    elements.giftSelect &&
                addToCartButton ===
                    elements.addToCartButton;

            if (elementsUnchanged) {
                return true;
            }

            unbindCurrentElements();

            appRoot = elements.appRoot;
            giftRow = elements.giftRow;
            giftSelect = elements.giftSelect;
            addToCartButton =
                elements.addToCartButton;
            errorElement =
                elements.errorElement;

            applyElementHooks(elements);

            giftSelect.addEventListener(
                "change",
                restoreLockedValue,
                true
            );

            giftSelect.addEventListener(
                "pointerdown",
                preventSelectInteraction,
                true
            );

            giftSelect.addEventListener(
                "mousedown",
                preventSelectInteraction,
                true
            );

            giftSelect.addEventListener(
                "touchstart",
                preventSelectInteraction,
                true
            );

            giftSelect.addEventListener(
                "keydown",
                preventSelectInteraction,
                true
            );

            debugLog("이벤트 등록 완료", {
                appRoot,
                giftRow,
                giftSelect,
                addToCartButton,
                errorElement
            });

            applyInitialSelection();

            return true;
        }

        /*
         * MutationObserver 중복 실행 축소
         */
        function scheduleBind() {
            if (
                state.destroyed ||
                observerFrame
            ) {
                return;
            }

            observerFrame = requestAnimationFrame(() => {
                observerFrame = null;

                const isBound = bindGiftElements();

                /*
                * ComboWise 요소를 찾고 이벤트 연결까지 완료했으면
                * 더 이상 DOM을 감시하지 않음.
                */
                if (isBound && observer) {
                    observer.disconnect();
                    observer = null;
                    observerRoot = null;

                    debugLog(
                        "✅ 요소 연결 완료: MutationObserver 종료"
                    );
                }
            });
        }

        /*
         * 모듈 초기화
         */
        function init() {
            if (state.destroyed) {
                return;
            }

            if (DEBUG) {
                console.groupCollapsed(
                    "🎁 [gift-priority-selector] 모듈 초기화"
                );

                console.log("module root:", root);
                console.log("target title:", targetTitle);
                console.log(
                    "gift product handle:",
                    giftProductHandle
                );
                console.log(
                    "inventory section:",
                    inventorySectionId
                );
                console.log(
                    "safety stock:",
                    safetyStock
                );
                console.log("priority:", priority);

                console.groupEnd();
            }

            if (!targetTitle) {
                debugError(
                    "data-dc-option-target-title이 비어 있음"
                );

                return;
            }

            if (!giftProductHandle) {
                debugError(
                    "data-dc-option-product-handle이 비어 있음"
                );

                return;
            }

            /*
            * ComboWise가 이미 렌더링되어 있으면
            * 즉시 연결하고 observer를 생성하지 않음.
            */
            const isBound = bindGiftElements();

            if (isBound) {
                debugLog(
                    "✅ 최초 탐색에서 요소 연결 완료"
                );

                return;
            }

            /*
            * ComboWise가 아직 렌더링되지 않은 경우에만
            * 렌더링 완료까지 임시 감시함.
            */
            const searchScope = getSearchScope();

            observerRoot =
                searchScope === document
                    ? document.body
                    : searchScope;

            observer = new MutationObserver(
                scheduleBind
            );

            observer.observe(observerRoot, {
                childList: true,
                subtree: true
            });

            debugLog(
                "ComboWise 렌더링 대기: MutationObserver 등록",
                {
                    observerRoot
                }
            );
        }

        /*
         * 모듈 제거
         */
        function destroy() {
            state.destroyed = true;
            state.applyingVariant = false;
            state.initialSelectionRunning = false;

            if (observerFrame) {
                cancelAnimationFrame(
                    observerFrame
                );

                observerFrame = null;
            }

            observer?.disconnect();
            observer = null;
            observerRoot = null;

            unbindCurrentElements();
            
            if (appRoot) {
                appRoot.classList.remove(
                    "dc-gift-priority"
                );
            }

            if (giftRow) {
                giftRow.classList.remove(
                    "dc-gift-priority__row"
                );
            }

            if (giftSelect) {
                giftSelect.classList.remove(
                    "dc-gift-priority__select",
                    "dc-gift-priority__select--locked"
                );

                giftSelect.removeAttribute(
                    "aria-disabled"
                );

                giftSelect.removeAttribute(
                    "tabindex"
                );

                delete giftSelect.dataset
                    .dcOptionGiftSelect;

                delete giftSelect.dataset
                    .dcOptionLockedValue;

                delete giftSelect.dataset
                    .dcOptionPriorityInitialized;
            }

            if (addToCartButton) {
                enableAddToCart();

                addToCartButton.classList.remove(
                    "dc-gift-priority__button",
                    "dc-gift-priority__button--disabled"
                );

                if (
                    addToCartButton.dataset
                        .dcAction ===
                    "add-priority-gift"
                ) {
                    delete addToCartButton.dataset
                        .dcAction;
                }
            }

            appRoot = null;
            giftRow = null;
            giftSelect = null;
            addToCartButton = null;
            errorElement = null;

            debugLog("모듈 destroy 완료");
        }

        return {
            init,
            destroy
        };
    };
})();